/**
 * @fileoverview Parsers de formatos espaciales (WKT, GeoJSON)
 * @module lib/coordinate/parsers/spatial
 * 
 * Extraído de coordinateNormalizer.ts v2.12 como parte de TD-002.C
 * 
 * Formatos soportados:
 * - WKT: POINT(x y) o POINT(x, y)
 * - GeoJSON: {"type":"Point","coordinates":[x,y]}
 * 
 * @version 1.0.0
 * @date 2026-01-26
 */

import type { ResultadoFormatoEspacial } from '../types';

// ============================================================================
// PARSERS DE FORMATOS ESPACIALES
// ============================================================================

/**
 * Parsea un valor WKT POINT a coordenadas X/Y
 * 
 * Soporta formatos:
 * - POINT(x y)
 * - POINT(x, y)
 * - point(x y) (case-insensitive)
 * 
 * @param valor - String en formato WKT POINT
 * @returns Objeto con coordenadas x, y o null si no es válido
 * 
 * @example
 * parseWKT("POINT(439501 4142536)")  // { x: 439501, y: 4142536 }
 * parseWKT("POINT(439501, 4142536)") // { x: 439501, y: 4142536 }
 * parseWKT("POINT(-3.5, 37.2)")      // { x: -3.5, y: 37.2 }
 */
export function parseWKT(valor: string): { x: number; y: number } | null {
  const wktRegex = /^\s*POINT\s*\(\s*(-?\d+\.?\d*)\s*[,\s]\s*(-?\d+\.?\d*)\s*\)\s*$/i;
  const match = valor.match(wktRegex);
  
  if (match) {
    const x = parseFloat(match[1]);
    const y = parseFloat(match[2]);
    if (!isNaN(x) && !isNaN(y)) {
      return { x, y };
    }
  }
  return null;
}

/**
 * Parsea un valor GeoJSON Point a coordenadas X/Y
 * 
 * Formato esperado:
 * {"type":"Point","coordinates":[x,y]}
 * 
 * @param valor - String JSON en formato GeoJSON Point
 * @returns Objeto con coordenadas x, y o null si no es válido
 * 
 * @example
 * parseGeoJSON('{"type":"Point","coordinates":[439501, 4142536]}')
 * // { x: 439501, y: 4142536 }
 */
export function parseGeoJSON(valor: string): { x: number; y: number } | null {
  try {
    const jsonStr = valor.trim();
    if (!jsonStr.startsWith('{') || !jsonStr.endsWith('}')) {
      return null;
    }
    const obj = JSON.parse(jsonStr);
    if (obj.type?.toLowerCase() === 'point' && 
        Array.isArray(obj.coordinates) && 
        obj.coordinates.length >= 2) {
      const x = parseFloat(obj.coordinates[0]);
      const y = parseFloat(obj.coordinates[1]);
      if (!isNaN(x) && !isNaN(y)) {
        return { x, y };
      }
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Detecta y parsea formatos espaciales (WKT, GeoJSON)
 * 
 * @param valor - Valor a analizar
 * @returns Resultado con coordenadas extraídas y formato detectado
 * 
 * @example
 * detectarFormatoEspacial("POINT(439501 4142536)")
 * // { esEspacial: true, valorX: 439501, valorY: 4142536, formato: 'WKT_POINT', warning: '...' }
 */
export function detectarFormatoEspacial(valor: string): ResultadoFormatoEspacial {
  const resultado: ResultadoFormatoEspacial = {
    esEspacial: false,
    valorX: null,
    valorY: null,
    formato: null,
    warning: null,
  };
  
  if (typeof valor !== 'string') return resultado;
  
  const v = valor.trim();
  
  const wkt = parseWKT(v);
  if (wkt) {
    resultado.esEspacial = true;
    resultado.valorX = wkt.x;
    resultado.valorY = wkt.y;
    resultado.formato = 'WKT_POINT';
    resultado.warning = `WKT POINT detectado: ${v} → X=${wkt.x}, Y=${wkt.y}`;
    return resultado;
  }
  
  const geojson = parseGeoJSON(v);
  if (geojson) {
    resultado.esEspacial = true;
    resultado.valorX = geojson.x;
    resultado.valorY = geojson.y;
    resultado.formato = 'GEOJSON_POINT';
    resultado.warning = `GeoJSON Point detectado → X=${geojson.x}, Y=${geojson.y}`;
    return resultado;
  }
  
  return resultado;
}
